// Client-side IRT engine for Preview mode.
//
// Implements the Graded Response Model (GRM) with EAP (Expected A Posteriori)
// theta estimation over a fixed quadrature grid — the same model family the
// external Adaptest scoring service uses, so preview numbers line up with the
// service's conventions (theta on a standard-normal scale, SEM as posterior
// SD, T-score = theta x 10 + 50).
//
// This runs entirely in the browser: preview is a simulation tool, so it must
// work without a scoring endpoint and update instantly on every answer.

import { getResponseKey } from './skipLogic';

// Quadrature grid: 81 points over [-4, 4] with a standard-normal prior.
const GRID_MIN = -4;
const GRID_MAX = 4;
const GRID_POINTS = 81;

const THETA_GRID = Array.from(
  { length: GRID_POINTS },
  (_, i) => GRID_MIN + (i * (GRID_MAX - GRID_MIN)) / (GRID_POINTS - 1)
);
const PRIOR = THETA_GRID.map((t) => Math.exp(-0.5 * t * t));

const UNUSED_THRESHOLD = -99;

const isUsableNumber = (v) => typeof v === 'number' && Number.isFinite(v) && v !== UNUSED_THRESHOLD;

/**
 * Normalize a raw irt_parameters dict into { slope, thresholds } or null.
 *
 * Accepts both storage shapes seen in the item bank:
 *   - { slope | discrimination, thresholds: [...] }
 *   - { discrimination, c1..cN } with -99 marking unused categories
 */
export const normalizeIrtParams = (irt) => {
  if (!irt || typeof irt !== 'object') return null;

  const slope = Number(
    irt.slope ?? irt.discrimination ?? (Array.isArray(irt.slopes) ? irt.slopes[0] : undefined)
  );
  if (!Number.isFinite(slope) || slope <= 0) return null;

  let thresholds = Array.isArray(irt.thresholds)
    ? irt.thresholds.map(Number).filter(isUsableNumber)
    : [];

  if (thresholds.length === 0) {
    // Fall back to c1..cN keys, in numeric order.
    thresholds = Object.keys(irt)
      .filter((k) => /^c\d+$/.test(k))
      .sort((a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10))
      .map((k) => Number(irt[k]))
      .filter(isUsableNumber);
  }

  if (thresholds.length === 0) return null;
  // GRM requires ascending category boundaries; some source files export
  // C_1..C_n highest-first, so sort rather than trust file order.
  thresholds.sort((a, b) => a - b);
  return { slope, thresholds };
};

/**
 * Collect every question element across the screens that carries usable IRT
 * parameters. Returns [] when the assessment has no IRT data at all — callers
 * use that to hide the IRT UI entirely.
 */
export const collectIrtItems = (screens) => {
  const items = [];
  (screens || []).forEach((screen, screenIndex) => {
    (screen.elements || []).forEach((el) => {
      if (el.type !== 'question') return;
      const irt = el.data?.irt_parameters || el.itemData?.irt_parameters;
      const params = normalizeIrtParams(irt);
      if (!params) return;

      const options = [el.responseOptions, el.data?.options, el.itemData?.options].find(Array.isArray) || [];
      items.push({
        responseKey: getResponseKey(el),
        code: el.code || el.data?.code || el.itemData?.code || null,
        text: el.content || el.data?.text || el.itemData?.text || '',
        screenIndex,
        screenName: screen.name || `Screen ${screenIndex + 1}`,
        slope: params.slope,
        thresholds: params.thresholds,
        options,
      });
    });
  });
  return items;
};

/**
 * Map a raw preview response value to a 0-indexed GRM category, or null when
 * the item is unanswered / the value can't be mapped. Category order follows
 * the displayed option order (the calibration convention for these banks).
 */
export const responseToCategoryIndex = (item, value) => {
  if (value === undefined || value === null || value === '') return null;
  const v = Array.isArray(value) ? value[0] : value;

  const idx = item.options.findIndex(
    // eslint-disable-next-line eqeqeq
    (opt) => (typeof opt === 'object' && opt !== null ? opt.value : opt) == v
  );
  if (idx !== -1) return idx;

  // No option match — accept a bare numeric category if it's in range.
  const nCats = item.thresholds.length + 1;
  const num = Number(v);
  if (Number.isInteger(num) && num >= 0 && num < nCats) return num;
  return null;
};

// GRM category probabilities for one item at one theta.
// P*(k) = logistic(a * (theta - b_k)); P(cat k) = P*(k) - P*(k+1),
// with boundary conventions P*(0) = 1 and P*(K) = 0.
const categoryProbabilities = (slope, thresholds, theta) => {
  const stars = [1, ...thresholds.map((b) => 1 / (1 + Math.exp(-slope * (theta - b)))), 0];
  const probs = [];
  for (let k = 0; k < stars.length - 1; k++) {
    // Floor to keep the likelihood strictly positive under extreme params.
    probs.push(Math.max(stars[k] - stars[k + 1], 1e-8));
  }
  return probs;
};

// Fisher information for a GRM item at theta:
// I(theta) = a^2 * sum_k ( (w_k - w_{k+1})^2 / P_k ), where w = P*(1 - P*).
const itemInformation = (slope, thresholds, theta) => {
  const stars = [1, ...thresholds.map((b) => 1 / (1 + Math.exp(-slope * (theta - b)))), 0];
  const w = stars.map((p) => p * (1 - p));
  let info = 0;
  for (let k = 0; k < stars.length - 1; k++) {
    const p = Math.max(stars[k] - stars[k + 1], 1e-8);
    const num = w[k] - w[k + 1];
    info += (num * num) / p;
  }
  return slope * slope * info;
};

/**
 * Score the current preview responses against the assessment's IRT items.
 *
 * Returns null when no IRT item has been answered yet (theta would just be
 * the prior). Otherwise:
 * {
 *   theta, sem, tScore, tScoreSem,      // EAP estimate + posterior SD
 *   answeredCount, totalCount,
 *   testInformation,                     // sum of item info at theta-hat
 *   items: [{ ...item, categoryIndex, answered, information }]
 * }
 */
export const scoreIrtResponses = (irtItems, responses) => {
  if (!irtItems || irtItems.length === 0) return null;

  const scored = irtItems.map((item) => ({
    ...item,
    categoryIndex: responseToCategoryIndex(item, responses?.[item.responseKey]),
  }));
  const answered = scored.filter((it) => it.categoryIndex !== null);
  if (answered.length === 0) return null;

  // Posterior over the theta grid.
  const posterior = THETA_GRID.map((theta, gi) => {
    let like = PRIOR[gi];
    for (const it of answered) {
      like *= categoryProbabilities(it.slope, it.thresholds, theta)[it.categoryIndex];
    }
    return like;
  });

  const totalMass = posterior.reduce((a, b) => a + b, 0);
  const theta = posterior.reduce((acc, w, gi) => acc + w * THETA_GRID[gi], 0) / totalMass;
  const variance =
    posterior.reduce((acc, w, gi) => acc + w * (THETA_GRID[gi] - theta) ** 2, 0) / totalMass;
  const sem = Math.sqrt(variance);

  let testInformation = 0;
  const items = scored.map((it) => {
    const information = itemInformation(it.slope, it.thresholds, theta);
    if (it.categoryIndex !== null) testInformation += information;
    return { ...it, answered: it.categoryIndex !== null, information };
  });

  return {
    theta,
    sem,
    tScore: theta * 10 + 50,
    tScoreSem: sem * 10,
    answeredCount: answered.length,
    totalCount: irtItems.length,
    testInformation,
    items,
  };
};
